/**
 * First-visit orientation popover — UX5 of the 2026-06-01 holistic mesh
 * redesign.
 *
 * The mesh has 5 lenses, a synthetic-vs-detected node distinction,
 * shift-click-to-zoom, edge-confidence filters, and a per-tenant /
 * per-analysis scope split. Without a one-pass orientation, first-time
 * users don't realize any of these exist.
 *
 * We use localStorage so the popover only auto-appears on first visit.
 * The "How to read this view" chip stays in the toolbar permanently so
 * the orientation is reachable later. The "Don't show again" action
 * sets the flag without dismissing.
 */
import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Paper,
  Popover,
  Stack,
  Typography,
} from '@mui/material'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import CloseIcon from '@mui/icons-material/Close'

const STORAGE_KEY = 'mesh.orientationHint.dismissed.v1'

interface OrientationHintProps {
  scope: 'analysis' | 'tenant'
}

export default function OrientationHint({ scope }: OrientationHintProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const [chipEl, setChipEl] = useState<HTMLElement | null>(null)

  // On first mount, auto-open if user hasn't dismissed before. We anchor
  // to the chip element (set via ref callback) once it's mounted.
  useEffect(() => {
    if (!chipEl) return
    let dismissed = false
    try {
      dismissed = localStorage.getItem(STORAGE_KEY) === '1'
    } catch {
      // localStorage may be unavailable (private browsing, etc.) — fail
      // open so the orientation still shows. Users can click X.
    }
    if (!dismissed) setAnchor(chipEl)
  }, [chipEl])

  const dismiss = (persist: boolean) => {
    if (persist) {
      try {
        localStorage.setItem(STORAGE_KEY, '1')
      } catch {
        // ignore — popover will reappear next visit, which is acceptable
      }
    }
    setAnchor(null)
  }

  return (
    <>
      <Chip
        ref={setChipEl}
        size="small"
        icon={<HelpOutlineIcon sx={{ fontSize: 14 }} />}
        label="How to read this view"
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          height: 24,
          fontSize: 11,
          fontWeight: 500,
          cursor: 'pointer',
          bgcolor: 'info.50',
          borderColor: 'info.main',
          border: 1,
          '&:hover': { bgcolor: 'info.100' },
        }}
      />
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => dismiss(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { maxWidth: 460 } } }}
      >
        <Paper sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Reading the agent topology
            </Typography>
            <IconButton size="small" onClick={() => dismiss(false)} aria-label="Close">
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Stack>

          <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.5 }}>
            {scope === 'analysis'
              ? 'This is the agent topology for THIS scan: every detected agent, the LLM models / tools / memory / APIs it touches, and the MCP / A2A servers it talks to.'
              : 'This is your full agent topology across every repo + every scan in this tenant. Use it to see how agents connect tenant-wide.'}
          </Typography>

          <Divider sx={{ my: 1 }} />

          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
            Five lenses, five questions:
          </Typography>
          <Box component="ul" sx={{ pl: 2, my: 0.5, '& li': { mb: 0.25 } }}>
            <li>
              <Typography variant="caption">
                <strong>Risk</strong> — where security findings concentrate
              </Typography>
            </li>
            <li>
              <Typography variant="caption">
                <strong>Blast Radius</strong> — if this agent misbehaves, what cascades
              </Typography>
            </li>
            <li>
              <Typography variant="caption">
                <strong>Drift</strong> — what has changed recently (last 30 days)
              </Typography>
            </li>
            <li>
              <Typography variant="caption">
                <strong>Compliance</strong> — which agents fail a framework's controls
              </Typography>
            </li>
            <li>
              <Typography variant="caption">
                <strong>Framework</strong> — tech-stack breakdown
              </Typography>
            </li>
          </Box>

          <Divider sx={{ my: 1 }} />

          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
            Interactions:
          </Typography>
          <Box component="ul" sx={{ pl: 2, my: 0.5, '& li': { mb: 0.25 } }}>
            <li>
              <Typography variant="caption">
                <strong>Click</strong> any node → details panel
              </Typography>
            </li>
            <li>
              <Typography variant="caption">
                <strong>Shift-click</strong> any node → zoom to its neighborhood
              </Typography>
            </li>
            <li>
              <Typography variant="caption">
                <strong>Solid edges</strong> are observed; <strong>dashed</strong> are inferred (use Edges filter)
              </Typography>
            </li>
            <li>
              <Typography variant="caption">
                <strong>Dashed-border nodes</strong> are external (MCP / A2A / model providers)
              </Typography>
            </li>
          </Box>

          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1.5 }}>
            <Button size="small" onClick={() => dismiss(false)} sx={{ textTransform: 'none' }}>
              Close
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={() => dismiss(true)}
              sx={{ textTransform: 'none' }}
            >
              Got it — don't show again
            </Button>
          </Stack>
        </Paper>
      </Popover>
    </>
  )
}
